// Чистые помощники колец охвата и окон пролёта (раздел «Спутники»). Без MapLibre
// и React — только геометрия геодезического круга и детерминированные UTC-подписи
// пролётов, чтобы всё покрывалось юнит-тестами. Отрисовка колец и таймлайн живут
// в SatellitesPage.tsx / PassesPanel.tsx.

import type { SatellitePass } from '../api'
import { t } from '../i18n'

const EARTH_RADIUS_KM = 6371
const DEG = Math.PI / 180

/** Точка назначения по большому кругу: из (lat,lon) уйти на дугу radiusKm по азимуту
 *  bearingDeg. Формула destination point на сфере R=6371. Проецируется в корректный
 *  овал на Меркаторе (к полюсам растянут по долготе). Долготу НЕ нормируем к
 *  [-180,180): непрерывное кольцо (напр. 179°→181°) MapLibre сам заворачивает через
 *  шов, а независимая нормировка каждой вершины порвала бы кольцо ложной полосой
 *  поперёк карты (та же беда, что у трека орбиты на антимеридиане). */
function destinationPoint(
  lat: number,
  lon: number,
  radiusKm: number,
  bearingDeg: number,
): [number, number] {
  const angular = radiusKm / EARTH_RADIUS_KM
  const theta = bearingDeg * DEG
  const phi1 = lat * DEG
  const lambda1 = lon * DEG
  const sinPhi2 =
    Math.sin(phi1) * Math.cos(angular) + Math.cos(phi1) * Math.sin(angular) * Math.cos(theta)
  const phi2 = Math.asin(sinPhi2)
  const y = Math.sin(theta) * Math.sin(angular) * Math.cos(phi1)
  const x = Math.cos(angular) - Math.sin(phi1) * sinPhi2
  const lambda2 = lambda1 + Math.atan2(y, x)
  return [lambda2 / DEG, phi2 / DEG]
}

/** Геодезический круг радиуса radiusKm вокруг (lat,lon) как замкнутый полигон,
 *  сэмплированный по points азимутам (0…360°). Кольцо ЗАМКНУТО: последняя вершина
 *  == первая (та же пара чисел), поэтому в кольце ровно points+1 координат.
 *  Возвращает Feature<Polygon> с пустыми свойствами. */
export function circlePolygon(
  lat: number,
  lon: number,
  radiusKm: number,
  points = 64,
): GeoJSON.Feature<GeoJSON.Polygon> {
  const ring: [number, number][] = []
  for (let i = 0; i < points; i++) {
    ring.push(destinationPoint(lat, lon, radiusKm, (360 * i) / points))
  }
  // Замыкаем ИМЕННО первой вершиной, а не счётом bearing=360°: из-за float 360·DEG
  // ≠ ровно 2π, вершина при 360° чуть отличалась бы от вершины при 0°, и кольцо было
  // бы «почти замкнуто». Копией первой пары гарантируем точное замыкание.
  ring.push([ring[0][0], ring[0][1]])
  return {
    type: 'Feature',
    geometry: { type: 'Polygon', coordinates: [ring] },
    properties: {},
  }
}

/** Минимально нужный срез track.current для колец охвата (altitude_km и т.п. не
 *  нужны; структурная типизация пускает сюда весь current как надмножество). */
export interface CoverageCurrent {
  lat: number
  lon: number
  footprint_radius_km: number
  dead_zone_radius_km: number
}

export interface CoverageOptions {
  points?: number
}

/** Кольца охвата выбранного спутника: внешнее (footprint, `ring:'footprint'`) и
 *  внутренняя мёртвая зона (dead-zone, `ring:'deadzone'`) вокруг центра current.
 *  Свойство ring нужно для data-driven стиля слоя. Нефинитный центр → пустая
 *  коллекция; кольцо с нефинитным/неположительным радиусом пропускаем. */
export function coverageFC(
  current: CoverageCurrent,
  opts: CoverageOptions = {},
): GeoJSON.FeatureCollection {
  const { lat, lon, footprint_radius_km, dead_zone_radius_km } = current
  if (!Number.isFinite(lat) || !Number.isFinite(lon)) {
    return { type: 'FeatureCollection', features: [] }
  }
  const points = opts.points ?? 64
  const rings: Array<{ ring: 'footprint' | 'deadzone'; radiusKm: number }> = [
    { ring: 'footprint', radiusKm: footprint_radius_km },
    { ring: 'deadzone', radiusKm: dead_zone_radius_km },
  ]
  const features: GeoJSON.Feature[] = []
  for (const { ring, radiusKm } of rings) {
    if (!Number.isFinite(radiusKm) || radiusKm <= 0) continue
    const f = circlePolygon(lat, lon, radiusKm, points)
    f.properties = { ring }
    features.push(f)
  }
  return { type: 'FeatureCollection', features }
}

function pad2(n: number): string {
  return String(n).padStart(2, '0')
}

/** UTC-день (YYYY-MM-DD) момента ISO. */
function utcDay(iso: string): string {
  const d = new Date(iso)
  return `${d.getUTCFullYear()}-${pad2(d.getUTCMonth() + 1)}-${pad2(d.getUTCDate())}`
}

/** UTC-время HH:MM момента ISO. */
function utcHM(iso: string): string {
  const d = new Date(iso)
  return `${pad2(d.getUTCHours())}:${pad2(d.getUTCMinutes())}`
}

export interface PassWindow {
  day: string
  startHM: string
  endHM: string
  durationLabel: string
}

/** Окно пролёта → детерминированные UTC-подписи для таймлайна: день, HH:MM начала
 *  и конца, длительность «N мин». UTC — тот же инстант всегда даёт ту же строку
 *  (независимо от часового пояса рантайма/теста). */
export function formatPassWindow(p: SatellitePass): PassWindow {
  return {
    day: utcDay(p.start),
    startHM: utcHM(p.start),
    endHM: utcHM(p.end),
    durationLabel: t('satellites.minutes', { count: Math.round(p.duration_min) }),
  }
}

export interface PassDayGroup {
  day: string
  passes: SatellitePass[]
}

/** Группирует пролёты по UTC-дню начала. Дни и пролёты внутри дня — по возрастанию
 *  времени начала (детерминированно, ISO-строки сортируются лексикографически). */
export function groupPassesByDay(passes: SatellitePass[]): PassDayGroup[] {
  const byDay = new Map<string, SatellitePass[]>()
  for (const p of passes) {
    const day = utcDay(p.start)
    const bucket = byDay.get(day)
    if (bucket) bucket.push(p)
    else byDay.set(day, [p])
  }
  return Array.from(byDay.entries())
    .map(([day, list]) => ({
      day,
      passes: [...list].sort((a, b) => a.start.localeCompare(b.start)),
    }))
    .sort((a, b) => a.day.localeCompare(b.day))
}
